// ── Thin, resilient REST client for apps/api (VITE_API_URL). ──
// Every call degrades gracefully: if VITE_API_URL is unset, the server is offline, or the
// response is malformed, the helper resolves to a safe fallback (null / []) and never throws.
// This keeps single-player fully functional with no api/server running.

import type {
  QuestDef,
  QuestProgress,
  MarketListing,
  Player,
  InventorySlot,
  TokenConfigResponse,
  TokenBalanceResponse,
  MintableBeast,
  MintStatusResponse,
  MintBeastResponse,
  OwnedBeastsResponse,
  StakeListResponse,
  StakeMutationResponse,
  StakeClaimResponse,
  PayWith,
} from '@mn/shared';
import { getSessionToken } from './auth.js';

const API_URL: string | undefined = import.meta.env.VITE_API_URL;

/** True only when an api base URL is configured. */
export function apiConfigured(): boolean {
  return typeof API_URL === 'string' && API_URL.length > 0;
}

/**
 * Merge the Phase-1 wallet-auth `Authorization: Bearer <token>` header into a request's headers
 * WHEN a session token exists. Single source of truth so every call carries it. When the player
 * is tokenless (no provider / declined) this is a pure pass-through — requests look exactly as
 * they did before auth existed, which the Phase-1 server still accepts.
 * @param {Record<string, string>} [headers]
 * @returns {Record<string, string>}
 */
function withAuth(headers: Record<string, string> = {}): Record<string, string> {
  const token = getSessionToken();
  return token ? { ...headers, authorization: `Bearer ${token}` } : headers;
}

/** Distinguishes "no account yet" (HTTP 404) from any other failure. */
export const NOT_FOUND = Symbol('not-found');

async function apiGet<T>(path: string): Promise<T | null> {
  if (!apiConfigured()) return null;
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'GET',
      headers: withAuth({ accept: 'application/json' }),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (err) {
    console.warn(`[api] GET ${path} failed — degrading to offline`, err);
    return null;
  }
}

/** GET that surfaces a 404 (NOT_FOUND) distinctly from other errors (null). */
async function apiGetOrNotFound<T>(path: string): Promise<T | null | typeof NOT_FOUND> {
  if (!apiConfigured()) return null;
  try {
    // 6s timeout so the entry screen never hangs on a slow/down api (→ offline → setup).
    const res = await fetch(`${API_URL}${path}`, {
      method: 'GET',
      headers: withAuth({ accept: 'application/json' }),
      signal: AbortSignal.timeout(12000),
    });
    if (res.status === 404) return NOT_FOUND;
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (err) {
    console.warn(`[api] GET ${path} failed — degrading to offline`, err);
    return null;
  }
}

async function apiPost<T>(path: string, body: unknown): Promise<T | null> {
  if (!apiConfigured()) return null;
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (err) {
    console.warn(`[api] POST ${path} failed — degrading to offline`, err);
    return null;
  }
}

/** A daily quest the client shows: the definition plus this player's progress. */
export interface DailyQuest {
  def: QuestDef;
  progress?: QuestProgress;
}

export interface PlayerSummary {
  wallet?: string;
  gold?: number;
}

/** GET /player?wallet= — authoritative Gold balance (null offline). */
export function fetchPlayer(wallet: string | null): Promise<PlayerSummary | null> {
  const q = wallet ? `?wallet=${encodeURIComponent(wallet)}` : '';
  return apiGet<PlayerSummary>(`/player${q}`);
}

/** Full account view returned by the player endpoints (player + inventory aggregate). */
export interface PlayerView {
  player: Player;
  inventory: InventorySlot[];
}

/** Profile fields the account setup / edit screens write. `avatar` is a PLAYER_SKIN_* key. */
export interface ProfileInput {
  wallet: string;
  username?: string;
  avatar?: string;
  bio?: string;
  /** Inviter wallet (from the landing's ?ref=). Honored ONCE on first account creation. */
  ref?: string;
}

/**
 * GET /player?wallet= — load the full account (profile + party + inventory).
 * Resolves to NOT_FOUND when the wallet has no account yet (HTTP 404), null on any
 * other failure / offline, or the {@link PlayerView} on success.
 */
export function getPlayerProfile(wallet: string): Promise<PlayerView | null | typeof NOT_FOUND> {
  return apiGetOrNotFound<PlayerView>(`/player?wallet=${encodeURIComponent(wallet)}`);
}

/** POST /player — create the account for a new wallet. Returns the created view (201) or null. */
export function createPlayerProfile(input: ProfileInput): Promise<PlayerView | null> {
  return apiPost<PlayerView>('/player', input);
}

/** POST /player/profile — edit name / PFP / bio on an existing account. Returns the view or null. */
export function updatePlayerProfile(input: ProfileInput): Promise<PlayerView | null> {
  return apiPost<PlayerView>('/player/profile', input);
}

/** GET /quests/daily?wallet= — today's quests (null offline → caller uses demo set). */
export async function fetchDailyQuests(wallet: string | null): Promise<DailyQuest[] | null> {
  const q = wallet ? `?wallet=${encodeURIComponent(wallet)}` : '';
  const res = await apiGet<DailyQuest[]>(`/quests/daily${q}`);
  // The api can answer with a non-array payload (an error/object body); normalize to an array or null
  // so callers never do `.find`/`.map` on a non-array (that threw "t.find is not a function").
  return Array.isArray(res) ? res : null;
}

/** POST /quests/claim — claim a completed quest's reward. Returns updated player on success. */
export function claimQuest(wallet: string | null, questId: string): Promise<PlayerSummary | null> {
  return apiPost<PlayerSummary>('/quests/claim', { wallet, questId });
}

/** Battle events the game reports to advance daily quest progress server-side. */
export type QuestEvent = 'battle_win' | 'monster_caught';

/**
 * POST /quests/progress — report a battle outcome so matching daily quests advance + persist.
 * `monsterId` (the defeated/caught species id) lets the server match species-specific quests;
 * generic quests advance regardless. Degrades to null offline / walletless and never throws, so
 * single-player is unaffected. Returns the player's updated quests on success.
 */
export function postQuestProgress(
  wallet: string | null,
  event: QuestEvent,
  monsterId?: number | string
): Promise<DailyQuest[] | null> {
  if (!wallet) return Promise.resolve(null);
  const body: { wallet: string; event: QuestEvent; monsterId?: string } = { wallet, event };
  if (monsterId !== undefined && monsterId !== null) {
    body.monsterId = String(monsterId);
  }
  return apiPost<DailyQuest[]>('/quests/progress', body);
}

/** GET /market — open marketplace listings (null offline → empty board). */
export function fetchMarketListings(): Promise<MarketListing[] | null> {
  return apiGet<MarketListing[]>('/market');
}

/** A leaderboard row as returned by GET /leaderboard. */
export interface LeaderboardEntry {
  rank: number;
  username: string;
  gold: number;
  avatar?: string;
}

/** GET /leaderboard?limit= — top players by Gold (DESC). Resolves to [] offline / on error. */
export async function fetchLeaderboard(limit = 20): Promise<LeaderboardEntry[]> {
  const n = Math.max(1, Math.min(50, Math.round(limit)));
  const rows = await apiGet<LeaderboardEntry[]>(`/leaderboard?limit=${n}`);
  return Array.isArray(rows) ? rows : [];
}

/** The caller's own leaderboard standing as returned by GET /leaderboard/me. */
export interface PlayerRank {
  rank: number;
  gold: number;
  total: number;
}

/** GET /leaderboard/me?wallet= — the player's own rank/gold/total (null offline / walletless). */
export function fetchMyRank(wallet: string | null): Promise<PlayerRank | null> {
  if (!wallet) return Promise.resolve(null);
  return apiGet<PlayerRank>(`/leaderboard/me?wallet=${encodeURIComponent(wallet)}`);
}

// ── Card Shop (real graded Pokémon cards redeemable for Gold). ──

/** A single graded card the shop offers, priced in Gold (server-computed). */
export interface ShopCard {
  mint: string;
  name: string;
  grade: string;
  grader: string;
  valueUsd: number;
  imageUrl: string;
  priceGold: number;
  /** $TOKEN price (pegged through the discounted Gold price), present when the token feature is live. */
  priceToken?: number;
}

/** Shipping details captured when a player redeems a card IRL. All fields required. */
export interface ShippingInput {
  name: string;
  address: string;
  city: string;
  postal: string;
  country: string;
}

/** How the redeemed card is delivered: on-chain NFT transfer, or physical shipping. */
export type DeliveryMethod = 'onchain' | 'irl';

/** Lifecycle of a placed redemption order, mirrored by the team's fulfillment flow. */
export type ShopOrderStatus = 'pending_review' | 'approved' | 'shipped' | 'rejected';

/** A redemption order as returned by GET /shop/orders. Flat shape — matches the API's CardOrder
 *  exactly (services/shop.ts `toOrder`). Was nested (`card.name`/`goldSpent`) → mismatched the
 *  server so My Orders rendered "Card / 0 Gold / broken image". */
export interface ShopOrder {
  id?: string;
  cardMint?: string;
  cardName?: string;
  cardImage?: string;
  valueUsd?: number;
  priceGold?: number;
  status: ShopOrderStatus;
  deliveryMethod?: DeliveryMethod;
  tracking?: string | null;
  createdAt?: string;
}

/** Result of placing an order: 201 success, 'insufficient' on HTTP 400, null offline/error. */
export type PlaceOrderResult = { ok: true; order?: ShopOrder } | { ok: false; reason: 'insufficient' | 'error' };

/** A page of shop cards + holder discount + pagination metadata. */
export interface ShopCardsResult {
  cards: ShopCard[];
  holderDiscountPct: number;
  page: number;
  totalPages: number;
  total: number;
  category: string;
}

/**
 * GET /shop/cards — real graded cards by CATEGORY (Pokemon / One Piece / Sports), name-SEARCHable and
 * PAGINATED, priced in Gold. With a `wallet`, prices come back discounted by its $MONSTARA holder tier
 * (+ `holderDiscountPct`). Resolves to an empty page offline / on error so the shop never crashes.
 */
export async function fetchShopCards(
  opts: { category?: string; search?: string; page?: number; maxUsd?: number; wallet?: string | null } = {},
): Promise<ShopCardsResult> {
  const params = new URLSearchParams();
  if (opts.category) params.set('category', opts.category);
  if (opts.search && opts.search.trim()) params.set('search', opts.search.trim());
  if (typeof opts.page === 'number' && opts.page > 0) params.set('page', String(opts.page));
  if (typeof opts.maxUsd === 'number' && opts.maxUsd > 0) params.set('maxUsd', String(opts.maxUsd));
  if (opts.wallet) params.set('wallet', opts.wallet);
  const qs = params.toString();
  const res = await apiGet<{
    cards: ShopCard[];
    holderDiscountPct?: number;
    page?: number;
    totalPages?: number;
    total?: number;
    category?: string;
  }>(`/shop/cards${qs ? `?${qs}` : ''}`);
  return {
    cards: res?.cards ?? [],
    holderDiscountPct: res?.holderDiscountPct ?? 0,
    page: res?.page ?? 0,
    totalPages: res?.totalPages ?? 1,
    total: res?.total ?? 0,
    category: res?.category ?? opts.category ?? 'Pokemon',
  };
}

/** GET /shop/orders?wallet= — this player's redemption orders (null offline → empty list). */
export async function fetchShopOrders(wallet: string | null): Promise<ShopOrder[]> {
  if (!wallet) return [];
  const res = await apiGet<{ orders: ShopOrder[] }>(`/shop/orders?wallet=${encodeURIComponent(wallet)}`);
  return res?.orders ?? [];
}

/**
 * POST /shop/order — redeem a card for Gold (server deducts the balance).
 * Distinguishes a 400 "not enough gold" (→ {ok:false, reason:'insufficient'}) from other
 * failures so the UI can show a precise message. Never throws.
 */
export async function placeShopOrder(input: {
  wallet: string;
  card: { mint: string; name: string; imageUrl: string; valueUsd: number };
  deliveryMethod: DeliveryMethod;
  // Omitted for on-chain orders (NFT → wallet); required for IRL.
  shipping?: ShippingInput;
  // Twitter/X handle for fulfilment contact (collected for both delivery methods).
  twitter?: string;
  // 'gold' (default, off-chain debit) or 'token' (a verified player→treasury $TOKEN transfer).
  payWith?: PayWith;
  // The player→treasury $TOKEN transfer signature — required when payWith === 'token'.
  tokenPaymentSig?: string;
}): Promise<PlaceOrderResult> {
  if (!apiConfigured()) return { ok: false, reason: 'error' };
  try {
    const res = await fetch(`${API_URL}/shop/order`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify(input),
    });
    if (res.status === 201 || res.ok) {
      const order = (await res.json().catch(() => null)) as ShopOrder | null;
      return { ok: true, order: order ?? undefined };
    }
    if (res.status === 400) return { ok: false, reason: 'insufficient' };
    return { ok: false, reason: 'error' };
  } catch (err) {
    console.warn('[api] POST /shop/order failed — degrading to offline', err);
    return { ok: false, reason: 'error' };
  }
}

/**
 * POST /shop/gacha — order a gacha pack (tier €50/250/2500 + category) for Gold, shipped IRL.
 * Same result contract as placeShopOrder (400 → insufficient). Never throws.
 */
export async function placeGachaOrder(input: {
  wallet: string;
  tier: number;
  category: string;
  shipping: ShippingInput;
  twitter?: string;
  // Payment rail: 'gold' (default) or 'token'/'sol' (then paymentSig is the verified inbound tx).
  payWith?: 'gold' | 'token' | 'sol';
  paymentSig?: string;
}): Promise<PlaceOrderResult> {
  if (!apiConfigured()) return { ok: false, reason: 'error' };
  try {
    const res = await fetch(`${API_URL}/shop/gacha`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify(input),
    });
    if (res.status === 201 || res.ok) {
      const order = (await res.json().catch(() => null)) as ShopOrder | null;
      return { ok: true, order: order ?? undefined };
    }
    if (res.status === 400) return { ok: false, reason: 'insufficient' };
    return { ok: false, reason: 'error' };
  } catch (err) {
    console.warn('[api] POST /shop/gacha failed — degrading to offline', err);
    return { ok: false, reason: 'error' };
  }
}

// ── $TOKEN / SOL top-ups (gold packs + gold booster) via a verified inbound transfer ──

/** Config the client needs to BUILD inbound transfers (Token-2022 mint + treasury) + LIVE quotes.
 *  `token`/`sol` are the amounts to send for each item, computed server-side from the live market
 *  price (USD-anchored); re-fetch before paying if older than `quoteTtlMs`. */
export interface TopupQuote {
  usd: number; // charged (promo) USD
  token: number; // whole $TOKEN to send (promo)
  sol: number; // SOL to send (promo)
  regularUsd?: number; // pre-promo USD (show struck through)
  regularToken?: number; // pre-promo $TOKEN
  regularSol?: number; // pre-promo SOL
}
export interface TopupConfig {
  enabled: boolean;
  mint: string;
  treasury: string;
  decimals: number;
  programId: string;
  prices?: { sol: number; token: number };
  promoPct?: number; // launch discount %, for the UI badge
  quoteTtlMs?: number;
  packs: ({ id: string; gold: number } & TopupQuote)[];
  booster: ({ id: string; multiplier: number; battles: number } & TopupQuote) | null;
  gacha: ({ tier: number; gold: number } & TopupQuote)[];
}

/** GET /shop/topup-config — resolves to a disabled config offline / when the rail isn't configured. */
export async function fetchTopupConfig(): Promise<TopupConfig> {
  const res = await apiGet<TopupConfig>('/shop/topup-config');
  return (
    res ?? {
      enabled: false,
      mint: '',
      treasury: '',
      decimals: 6,
      programId: '',
      packs: [],
      booster: null,
      gacha: [],
    }
  );
}

async function postTopup(path: string, body: Record<string, unknown>): Promise<{ ok: boolean }> {
  if (!apiConfigured()) return { ok: false };
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify(body),
    });
    return { ok: res.ok };
  } catch (err) {
    console.warn(`[api] POST ${path} failed`, err);
    return { ok: false };
  }
}

/** POST /shop/buy-gold — credit a Gold pack after a verified token/SOL transfer. */
export function buyGoldPack(
  wallet: string,
  packId: string,
  payWith: 'token' | 'sol',
  signature: string,
): Promise<{ ok: boolean }> {
  return postTopup('/shop/buy-gold', { wallet, packId, payWith, signature });
}

/** POST /shop/buy-booster — activate the Gold booster after a verified token/SOL transfer. */
export function buyGoldBooster(
  wallet: string,
  payWith: 'token' | 'sol',
  signature: string,
): Promise<{ ok: boolean }> {
  return postTopup('/shop/buy-booster', { wallet, payWith, signature });
}

// ── Card Vault: stake real graded-card NFTs with us → passive Gold ──

/** A staked card as returned by the Card Vault endpoints. */
export interface VaultStake {
  id: string;
  assetMint: string;
  cardName: string | null;
  valueUsd: number;
  dailyGold: number;
  status: 'pending' | 'active' | 'unstake_requested' | 'returned' | 'rejected';
  accruedGold: number;
  canUnstakeAt: string | null;
  stakedAt: string | null;
  createdAt: string;
}

/** GET /card-vault/stakes?wallet= — the player's stakes + accrued Gold (null-safe → []). */
export async function fetchVaultStakes(wallet: string | null): Promise<VaultStake[]> {
  if (!wallet) return [];
  const res = await apiGet<{ stakes: VaultStake[] }>(`/card-vault/stakes?wallet=${encodeURIComponent(wallet)}`);
  return Array.isArray(res?.stakes) ? res.stakes : [];
}

/** POST /card-vault/stake — register a card NFT sent to the vault wallet (optionally with its tx sig). */
export async function stakeVaultCard(
  wallet: string,
  assetMint: string,
  signature?: string,
): Promise<VaultStake | null> {
  if (!apiConfigured()) return null;
  try {
    const res = await fetch(`${API_URL}/card-vault/stake`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify({ wallet, assetMint, signature }),
    });
    if (res.status === 201 || res.ok) return (await res.json()) as VaultStake;
    return null;
  } catch (err) {
    console.warn('[api] POST /card-vault/stake failed', err);
    return null;
  }
}

/** POST /card-vault/claim — credit accrued passive Gold. Returns the new balance + amount credited. */
export async function claimVaultGold(wallet: string): Promise<{ credited: number; gold: number } | null> {
  return apiPost<{ credited: number; gold: number }>('/card-vault/claim', { wallet });
}

/** POST /card-vault/unstake-request — ask for a staked card back (after the lockup). */
export async function requestVaultUnstake(wallet: string, stakeId: string): Promise<VaultStake | null> {
  return apiPost<VaultStake>('/card-vault/unstake-request', { wallet, stakeId });
}

/** Server-computed Gold reward for a battle win. POST /player/battle-win → { gold } (new balance). */
export async function postBattleWin(wallet: string | null, enemyLevel: number): Promise<number | null> {
  if (!wallet) return null;
  const res = await apiPost<{ gold: number }>('/player/battle-win', { wallet, enemyLevel: Math.max(1, Math.round(enemyLevel)) });
  return res && typeof res.gold === 'number' ? res.gold : null;
}

// ── Single-use battle sessions (server-authoritative enemy + Gold reward) ──
// These make a wild battle's enemy + reward non-forgeable/non-replayable. BOTH calls degrade to
// null on any failure (offline, walletless, timeout, malformed) and NEVER throw, so a tampered
// or unavailable server can never break a battle — the caller falls back to the legacy local
// enemy + /player/battle-win path. The start call is timeout-capped so a battle never stalls.

/** Result of opening a battle session: the server-chosen enemy + the session token. */
export interface BattleSessionStart {
  sessionId: string;
  monsterId: number;
  level: number;
}

/** A battle outcome reported to the server to close a single-use session. */
export type BattleSessionOutcome = 'win' | 'lose' | 'flee' | 'capture';

/**
 * POST /battle/start { wallet, encounterAreaId } → the server-chosen wild enemy + a single-use
 * sessionId, or null offline / walletless / on error / timeout. Capped at ~3s so a wild encounter
 * never hangs waiting on the api — on null the caller generates the enemy locally as today.
 */
export async function startBattleSession(
  wallet: string | null,
  encounterAreaId: number | string,
): Promise<BattleSessionStart | null> {
  if (!wallet || !apiConfigured()) return null;
  try {
    const res = await fetch(`${API_URL}/battle/start`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify({ wallet, encounterAreaId }),
      signal: AbortSignal.timeout(3000),
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as Partial<BattleSessionStart> | null;
    if (data && typeof data.sessionId === 'string' && typeof data.monsterId === 'number' && typeof data.level === 'number') {
      return { sessionId: data.sessionId, monsterId: data.monsterId, level: data.level };
    }
    return null;
  } catch (err) {
    console.warn('[api] POST /battle/start failed — degrading to local encounter', err);
    return null;
  }
}

/**
 * POST /battle/resolve { wallet, sessionId, outcome, capturedMonsterId? } → { gold } where gold is
 * the new authoritative balance (number) for win/capture, null for lose/flee. Returns
 * { gold: null } when the call itself failed (offline / rejected / replayed) so the caller keeps
 * the legacy /player/battle-win fallback. Never throws.
 */
export async function resolveBattleSession(
  wallet: string | null,
  sessionId: string,
  outcome: BattleSessionOutcome,
  capturedMonsterId?: number | string,
): Promise<{ gold: number | null } | null> {
  if (!wallet || !apiConfigured()) return null;
  try {
    const body: Record<string, unknown> = { wallet, sessionId, outcome };
    if (capturedMonsterId !== undefined && capturedMonsterId !== null) body.capturedMonsterId = capturedMonsterId;
    const res = await fetch(`${API_URL}/battle/resolve`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as { gold?: number | null } | null;
    if (data && (typeof data.gold === 'number' || data.gold === null)) return { gold: data.gold ?? null };
    return null;
  } catch (err) {
    console.warn('[api] POST /battle/resolve failed — degrading to local reward', err);
    return null;
  }
}

/** Persist a map gold pickup. POST /player/collect-gold → { gold } (new balance, server-capped ≤50). */
export async function collectGold(wallet: string | null, amount: number): Promise<number | null> {
  if (!wallet) return null;
  const res = await apiPost<{ gold: number }>('/player/collect-gold', { wallet, amount: Math.max(1, Math.min(50, Math.round(amount))) });
  return res && typeof res.gold === 'number' ? res.gold : null;
}

/**
 * Server-side party AUTOSAVE: persist the full client party (beasts + levels) so a refresh / new
 * device restores it. No-op for an empty party — the server also ignores empty, so a transient empty
 * state can never wipe a real team. Fire-and-forget; resolves false on any failure.
 */
export async function saveParty(
  wallet: string | null,
  party: unknown[],
  storage?: unknown[],
): Promise<boolean> {
  if (!wallet || !Array.isArray(party) || party.length === 0) return false;
  const res = await apiPost<{ ok: boolean }>('/player/party', {
    wallet,
    party,
    ...(Array.isArray(storage) ? { storage } : {}),
  });
  return !!res?.ok;
}

/**
 * Pay a flat Gold fee to heal the whole party (the HP restore is applied client-side after this
 * succeeds). Returns { ok, gold } on success, or { ok:false, reason:'insufficient' } if too poor.
 */
export async function healParty(
  wallet: string | null
): Promise<{ ok: boolean; gold?: number; cost?: number; reason?: string }> {
  if (!wallet) return { ok: false, reason: 'no_wallet' };
  const res = await apiPost<{ ok: boolean; gold?: number; cost?: number; reason?: string }>('/player/heal', { wallet });
  return res ?? { ok: false, reason: 'error' };
}

/** Pay a small Gold fee to feed a beast (the bond raise is applied client-side). */
export async function feedMonster(
  wallet: string | null
): Promise<{ ok: boolean; gold?: number; cost?: number; reason?: string }> {
  if (!wallet) return { ok: false, reason: 'no_wallet' };
  const res = await apiPost<{ ok: boolean; gold?: number; cost?: number; reason?: string }>('/player/feed', { wallet });
  return res ?? { ok: false, reason: 'error' };
}

/** A player's PUBLIC profile (shown in the friend popup). */
export interface PublicProfile {
  wallet: string;
  username: string;
  avatar: string;
  bio: string;
  level: number;
  monstersCount: number;
  gold: number;
  battlesWon: number;
  battlesLost: number;
  pvpWon: number;
  pvpLost: number;
  online: boolean;
  realm: string | null;
}

/** GET /player/public?wallet= → another player's public profile for the friend popup. null on error. */
export async function getPublicProfile(wallet: string): Promise<PublicProfile | null> {
  if (!wallet) return null;
  return apiGet<PublicProfile>(`/player/public?wallet=${encodeURIComponent(wallet)}`);
}

/** GET /players/search?q= → live username autocomplete (up to 8 { wallet, username }), excluding self. */
export async function searchPlayers(q: string, self: string | null): Promise<{ wallet: string; username: string }[]> {
  const t = q.trim();
  if (!t) return [];
  const res = await apiGet<{ wallet: string; username: string }[]>(
    `/players/search?q=${encodeURIComponent(t)}&self=${encodeURIComponent(self ?? '')}`,
  );
  return Array.isArray(res) ? res : [];
}

// ── Gold Item Shop (fixed SYSTEM items bought with Gold + the daily Gold Vault). ──

/** A buyable catalog item as returned by GET /shop/items (price + server effect summary). */
export interface ShopItem {
  id: string;
  name: string;
  description: string;
  priceGold: number;
  effect: { kind: 'inventory' | 'buff' | 'gold_vault' } & Record<string, unknown>;
}

/** GET /shop/items — the fixed Gold catalog. Resolves to [] offline so the panel renders empty. */
export async function fetchShopItems(): Promise<ShopItem[]> {
  const res = await apiGet<{ items: ShopItem[] }>('/shop/items');
  return Array.isArray(res?.items) ? res.items : [];
}

/** Gold Vault status: owned? + ms until the next +100 claim is allowed (0 = claimable now). */
export interface VaultStatus {
  owned: boolean;
  claimedAt: string | null;
  msUntilNextClaim: number;
}

/** GET /shop/vault?wallet= — Gold Vault state for the UI (null offline / walletless). */
export async function fetchVaultStatus(wallet: string | null): Promise<VaultStatus | null> {
  if (!wallet) return null;
  return apiGet<VaultStatus>(`/shop/vault?wallet=${encodeURIComponent(wallet)}`);
}

/** Result of buying an item: `ok` + new Gold balance (+ the granted stack so the Bag updates
 *  immediately), or a reason (insufficient / error). */
export type BuyItemResult =
  | { ok: true; gold: number; granted: string; grantedItem?: { itemId: string; qty: number } }
  | { ok: false; reason: 'insufficient' | 'error' };

/**
 * POST /shop/buy-item { wallet, itemId } — spend Gold, server applies the effect.
 * Distinguishes a 400 (not enough Gold / one-time item owned) from other failures. Never throws.
 */
export async function buyShopItem(wallet: string | null, itemId: string): Promise<BuyItemResult> {
  if (!wallet || !apiConfigured()) return { ok: false, reason: 'error' };
  try {
    const res = await fetch(`${API_URL}/shop/buy-item`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify({ wallet, itemId }),
    });
    if (res.ok) {
      const data = (await res.json().catch(() => null)) as
        | { gold?: number; granted?: string; grantedItem?: { itemId: string; qty: number } }
        | null;
      if (data && typeof data.gold === 'number')
        return { ok: true, gold: data.gold, granted: data.granted ?? '', grantedItem: data.grantedItem };
      return { ok: false, reason: 'error' };
    }
    if (res.status === 400) return { ok: false, reason: 'insufficient' };
    return { ok: false, reason: 'error' };
  } catch (err) {
    console.warn('[api] POST /shop/buy-item failed — degrading to offline', err);
    return { ok: false, reason: 'error' };
  }
}

/** A premium catalog item buyable with $TOKEN (GET /shop/premium-items). */
export interface PremiumItem {
  id: string;
  name: string;
  description: string;
  priceToken: number;
}

/** GET /shop/premium-items — $TOKEN catalog. Resolves to [] when the feature is offline/unconfigured. */
export async function fetchPremiumItems(): Promise<PremiumItem[]> {
  const res = await apiGet<{ items?: PremiumItem[] }>('/shop/premium-items');
  return Array.isArray(res?.items) ? res.items : [];
}

/**
 * POST /shop/buy-premium { wallet, itemId, tokenPaymentSig } — buy a $TOKEN premium item.
 * The caller has already signed + sent the player→treasury transfer and passes the signature;
 * the server re-prices, verifies the transfer, and grants the effect. Never throws.
 */
export async function buyPremiumItem(
  wallet: string | null,
  itemId: string,
  tokenPaymentSig: string,
): Promise<BuyItemResult> {
  if (!wallet || !apiConfigured()) return { ok: false, reason: 'error' };
  try {
    const res = await fetch(`${API_URL}/shop/buy-premium`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify({ wallet, itemId, tokenPaymentSig }),
    });
    if (res.ok) {
      const data = (await res.json().catch(() => null)) as { gold?: number; granted?: string } | null;
      if (data && typeof data.gold === 'number') return { ok: true, gold: data.gold, granted: data.granted ?? '' };
      return { ok: false, reason: 'error' };
    }
    if (res.status === 400) return { ok: false, reason: 'insufficient' };
    return { ok: false, reason: 'error' };
  } catch (err) {
    console.warn('[api] POST /shop/buy-item failed — degrading to offline', err);
    return { ok: false, reason: 'error' };
  }
}

/** Result of a Gold Vault claim: success + new balance, on-cooldown (with wait ms), or error. */
export type ClaimVaultResult =
  | { ok: true; gold: number }
  | { ok: false; reason: 'cooldown'; msUntilNextClaim: number }
  | { ok: false; reason: 'error' };

/**
 * POST /shop/claim-vault { wallet } — claim +100 Gold (server enforces the 24h cooldown).
 * A 400 carries `msUntilNextClaim` so the UI can show the remaining time. Never throws.
 */
export async function claimGoldVault(wallet: string | null): Promise<ClaimVaultResult> {
  if (!wallet || !apiConfigured()) return { ok: false, reason: 'error' };
  try {
    const res = await fetch(`${API_URL}/shop/claim-vault`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify({ wallet }),
    });
    if (res.ok) {
      const data = (await res.json().catch(() => null)) as { gold?: number } | null;
      if (data && typeof data.gold === 'number') return { ok: true, gold: data.gold };
      return { ok: false, reason: 'error' };
    }
    if (res.status === 400) {
      const data = (await res.json().catch(() => null)) as { msUntilNextClaim?: number } | null;
      return { ok: false, reason: 'cooldown', msUntilNextClaim: data?.msUntilNextClaim ?? 0 };
    }
    return { ok: false, reason: 'error' };
  } catch (err) {
    console.warn('[api] POST /shop/claim-vault failed — degrading to offline', err);
    return { ok: false, reason: 'error' };
  }
}

// ── Daily login reward (free +50 Gold once per 24h). ──

/** Daily login reward status: claimable now? + ms until the next claim (0 = claimable now). */
export interface DailyStatus {
  canClaim: boolean;
  msUntilNext: number;
  /** The reward up next in the rotating cycle (label for the UI; gold or an item stack). */
  reward?: { kind: 'gold' | 'item'; label: string; gold?: number; itemId?: string; qty?: number };
}

/** GET /player/daily?wallet= — daily reward status for the UI (null offline / walletless). */
export function fetchDailyStatus(wallet: string | null): Promise<DailyStatus | null> {
  if (!wallet) return Promise.resolve(null);
  return apiGet<DailyStatus>(`/player/daily?wallet=${encodeURIComponent(wallet)}`);
}

/** Result of a daily-reward claim: success + new balance, on-cooldown (with wait ms), or error. */
export type ClaimDailyResult =
  | { ok: true; gold: number; label: string; grantedItem?: { itemId: string; qty: number } }
  | { ok: false; reason: 'cooldown'; msUntilNext: number }
  | { ok: false; reason: 'error' };

/**
 * POST /player/claim-daily { wallet } — claim the free +50 Gold (server enforces the 24h window).
 * A 400 carries `msUntilNextClaim` so the UI can show the remaining time. Never throws.
 */
export async function claimDailyReward(wallet: string | null): Promise<ClaimDailyResult> {
  if (!wallet || !apiConfigured()) return { ok: false, reason: 'error' };
  try {
    const res = await fetch(`${API_URL}/player/claim-daily`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify({ wallet }),
    });
    if (res.ok) {
      const data = (await res.json().catch(() => null)) as {
        gold?: number;
        reward?: { label?: string };
        grantedItem?: { itemId: string; qty: number };
      } | null;
      if (data && typeof data.gold === 'number') {
        return {
          ok: true,
          gold: data.gold,
          label: data.reward?.label ?? '+Gold',
          grantedItem: data.grantedItem,
        };
      }
      return { ok: false, reason: 'error' };
    }
    if (res.status === 400) {
      const data = (await res.json().catch(() => null)) as { msUntilNextClaim?: number } | null;
      return { ok: false, reason: 'cooldown', msUntilNext: data?.msUntilNextClaim ?? 0 };
    }
    return { ok: false, reason: 'error' };
  } catch (err) {
    console.warn('[api] POST /player/claim-daily failed — degrading to offline', err);
    return { ok: false, reason: 'error' };
  }
}

// ── Friends & Invites (social graph + presence). ──

/** A friend as the Friends panel renders it: identity + live presence. */
export interface FriendEntry {
  wallet: string;
  username: string;
  online: boolean;
  lastSeen: string | null;
  /** The friend's current realm (town/wilds/…) so the panel can offer to join them there. */
  realm: string | null;
}

/** An incoming pending request (someone asked to add me). */
export interface IncomingRequest {
  wallet: string;
  username: string;
}

/** GET /friends?wallet= → my accepted friends (with presence) + incoming requests. */
export interface FriendsView {
  friends: FriendEntry[];
  incoming: IncomingRequest[];
}

/** GET /friends?wallet= — resolves to an empty view offline / walletless (panel renders empty). */
export async function fetchFriends(wallet: string | null): Promise<FriendsView> {
  if (!wallet) return { friends: [], incoming: [] };
  const res = await apiGet<FriendsView>(`/friends?wallet=${encodeURIComponent(wallet)}`);
  return {
    friends: Array.isArray(res?.friends) ? res.friends : [],
    incoming: Array.isArray(res?.incoming) ? res.incoming : [],
  };
}

/**
 * Result of a friend mutation: `ok` + a human-readable `error` when it failed (empty on success).
 * A flat shape (not a discriminated union) so callers can read `.error` after an `if (!ok)` guard
 * without TS checkJs narrowing complaints.
 */
export interface FriendActionResult {
  ok: boolean;
  error: string;
}

/** Shared POST that surfaces the server's `{ error }` message (so the UI can show "Already friends" etc). */
async function friendAction(path: string, body: unknown): Promise<FriendActionResult> {
  if (!apiConfigured()) return { ok: false, error: 'Offline' };
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: withAuth({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify(body),
    });
    if (res.ok) return { ok: true, error: '' };
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    return { ok: false, error: data?.error || 'Something went wrong' };
  } catch (err) {
    console.warn(`[api] POST ${path} failed — degrading to offline`, err);
    return { ok: false, error: 'Offline' };
  }
}

/** POST /friends/request { wallet, target } — `target` is a username OR wallet. */
export function sendFriendRequest(wallet: string | null, target: string): Promise<FriendActionResult> {
  if (!wallet) return Promise.resolve({ ok: false, error: 'Connect your wallet' });
  return friendAction('/friends/request', { wallet, target });
}

/** POST /friends/accept { wallet, requester } — accept a pending request sent to me. */
export function acceptFriend(wallet: string | null, requester: string): Promise<FriendActionResult> {
  if (!wallet) return Promise.resolve({ ok: false, error: 'Connect your wallet' });
  return friendAction('/friends/accept', { wallet, requester });
}

/** POST /friends/remove { wallet, other } — decline a request OR unfriend. */
export function removeFriend(wallet: string | null, other: string): Promise<FriendActionResult> {
  if (!wallet) return Promise.resolve({ ok: false, error: 'Connect your wallet' });
  return friendAction('/friends/remove', { wallet, other });
}

/** POST /player/heartbeat { wallet } — presence ping so friends see me online. Fire-and-forget. */
export function sendHeartbeat(wallet: string | null): Promise<unknown> {
  if (!wallet) return Promise.resolve(null);
  return apiPost('/player/heartbeat', { wallet });
}

// ── $TOKEN on-chain beasts (Vault: mint cNFT, stake, claim) + token balance. ──
// Every call fails CLOSED: an unconfigured/old/offline API resolves to the "disabled" shape so the
// Vault renders a calm "coming soon" state instead of crashing. The token mint + treasury never
// live in the client; the server advertises them via GET /token/config only when truly configured.

/** The "feature is off" config the Vault falls back to whenever the API can't confirm it's live. */
const TOKEN_DISABLED: TokenConfigResponse = Object.freeze({
  enabled: false,
  mintShort: '',
  treeShort: '',
  decimals: 6,
  treasury: '',
  mint: '',
});

/**
 * GET /token/config — probe whether the $TOKEN on-chain feature is live + the mint/treasury the
 * client needs to build a fee transfer. A missing/old endpoint or any error resolves to the
 * disabled shape (fail-closed), so the Vault shows "coming soon".
 */
export async function fetchTokenConfig(): Promise<TokenConfigResponse> {
  const res = await apiGet<TokenConfigResponse>('/token/config');
  if (!res || typeof res.enabled !== 'boolean') return TOKEN_DISABLED;
  return { ...TOKEN_DISABLED, ...res };
}

/** GET /token/balance?wallet= — live on-chain $TOKEN balance (UI units). 0 offline/walletless. */
export async function fetchTokenBalance(wallet: string | null): Promise<number> {
  if (!wallet) return 0;
  const res = await apiGet<TokenBalanceResponse>(`/token/balance?wallet=${encodeURIComponent(wallet)}`);
  return res && typeof res.uiAmount === 'number' ? res.uiAmount : 0;
}

/** GET /beast/mintable?wallet= — caught beasts the server says are eligible to mint (with fees). */
export async function fetchMintableBeasts(wallet: string | null): Promise<MintableBeast[]> {
  if (!wallet) return [];
  const res = await apiGet<{ beasts?: MintableBeast[] }>(`/beast/mintable?wallet=${encodeURIComponent(wallet)}`);
  return Array.isArray(res?.beasts) ? res.beasts : [];
}

/** GET /beast/mint/status?wallet= — the beastUids this wallet has already minted. */
export async function fetchMintStatus(wallet: string | null): Promise<string[]> {
  if (!wallet) return [];
  const res = await apiGet<MintStatusResponse>(`/beast/mint/status?wallet=${encodeURIComponent(wallet)}`);
  return Array.isArray(res?.minted) ? res.minted : [];
}

/**
 * POST /beast/mint { beastUid, feeSig } — mint a caught beast into a cNFT. The caller has already
 * signed + sent the $TOKEN fee transfer and passes its signature; the server verifies it, then
 * mints to the player's wallet. Returns { ok, assetId, reason } — never throws.
 */
export async function mintBeast(
  wallet: string | null,
  beastUid: string,
  feeSig: string,
): Promise<MintBeastResponse> {
  if (!wallet || !apiConfigured()) return { ok: false, assetId: null, reason: 'offline' };
  const res = await apiPost<MintBeastResponse>('/beast/mint', { wallet, beastUid, feeSig });
  return res ?? { ok: false, assetId: null, reason: 'error' };
}

/** GET /beast/owned?wallet= — owned beast cNFTs (candidates for staking). [] offline/disabled. */
export async function fetchOwnedBeasts(wallet: string | null): Promise<OwnedBeastsResponse['beasts']> {
  if (!wallet) return [];
  const res = await apiGet<OwnedBeastsResponse>(`/beast/owned?wallet=${encodeURIComponent(wallet)}`);
  return Array.isArray(res?.beasts) ? res.beasts : [];
}

/** GET /stake/list?wallet= — staked beasts + claimable total (server-computed accrual). */
export async function fetchStakes(wallet: string | null): Promise<StakeListResponse> {
  const empty: StakeListResponse = { staked: [], pendingToken: 0, poolPaused: false };
  if (!wallet) return empty;
  const res = await apiGet<StakeListResponse>(`/stake/list?wallet=${encodeURIComponent(wallet)}`);
  if (!res) return empty;
  return {
    staked: Array.isArray(res.staked) ? res.staked : [],
    pendingToken: typeof res.pendingToken === 'number' ? res.pendingToken : 0,
    poolPaused: !!res.poolPaused,
  };
}

/** POST /stake/lock { assetId } — start earning on an owned beast cNFT. */
export async function lockStake(wallet: string | null, assetId: string): Promise<StakeMutationResponse> {
  if (!wallet || !apiConfigured()) return { ok: false, reason: 'offline' };
  const res = await apiPost<StakeMutationResponse>('/stake/lock', { wallet, assetId });
  return res ?? { ok: false, reason: 'error' };
}

/** POST /stake/unlock { assetId } — stop earning (subject to the min-lock window). */
export async function unlockStake(wallet: string | null, assetId: string): Promise<StakeMutationResponse> {
  if (!wallet || !apiConfigured()) return { ok: false, reason: 'offline' };
  const res = await apiPost<StakeMutationResponse>('/stake/unlock', { wallet, assetId });
  return res ?? { ok: false, reason: 'error' };
}

/** POST /stake/claim — pay out the wallet's accrued $TOKEN (one real treasury→player transfer). */
export async function claimStake(wallet: string | null): Promise<StakeClaimResponse> {
  if (!wallet || !apiConfigured()) return { ok: false, paidToken: 0, txSig: null, reason: 'offline' };
  const res = await apiPost<StakeClaimResponse>('/stake/claim', { wallet });
  return res ?? { ok: false, paidToken: 0, txSig: null, reason: 'error' };
}
